import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  Call,
  CoachJoinMode,
  DialSession,
} from "@sales-ops-amplify/shared";
import {
  advanceDialSession,
  cancelDialSession,
  completeCall,
  dropVoicemail,
  getVoiceToken,
  prepareOutbound,
  updateCall,
} from "@/api/calls";
import { getCallConfig } from "@/api/call-config";

type SoftphoneStatus =
  | "idle"
  | "connecting"
  | "ready"
  | "ringing"
  | "in_call"
  | "post_call"
  | "error";

type DialRequest = {
  to: string;
  leadId?: string;
  leadName?: string;
  opportunityId?: string;
  accountId?: string;
  sequenceId?: string;
  enrollmentId?: string;
  useLocalPresence?: boolean;
};

type SoftphoneContextValue = {
  status: SoftphoneStatus;
  twilioConfigured: boolean;
  error: string | null;
  muted: boolean;
  activeCall: Call | null;
  dialSession: DialSession | null;
  setDialSession: (s: DialSession | null) => void;
  showPostCall: boolean;
  connectDevice: () => Promise<void>;
  dial: (req: DialRequest) => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
  sendDigits: (digits: string) => void;
  leaveVoicemail: (dropId: string) => Promise<void>;
  joinConference: (
    conferenceName: string,
    mode: CoachJoinMode,
    call: Call,
  ) => Promise<void>;
  startSessionDialing: (session: DialSession) => Promise<void>;
  skipDialSessionTarget: () => Promise<void>;
  cancelActiveDialSession: () => Promise<void>;
  openPostCall: (call: Call) => void;
  savePostCall: (input: {
    purposeId?: string;
    dispositionId?: string;
    notes?: string;
  }) => Promise<void>;
  dismissPostCall: () => void;
  lastConnectionError: string | null;
};

const SoftphoneContext = createContext<SoftphoneContextValue | null>(null);

export function useSoftphone(): SoftphoneContextValue {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) {
    throw new Error("useSoftphone must be used within SoftphoneProvider");
  }
  return ctx;
}

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SoftphoneStatus>("idle");
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [showPostCall, setShowPostCall] = useState(false);
  const [dialSession, setDialSession] = useState<DialSession | null>(null);
  const [lastConnectionError, setLastConnectionError] = useState<string | null>(
    null,
  );
  const [showConnectionErrors, setShowConnectionErrors] = useState(false);

  const deviceRef = useRef<import("@twilio/voice-sdk").Device | null>(null);
  const connectionRef = useRef<import("@twilio/voice-sdk").Call | null>(null);
  const dialSessionRef = useRef<DialSession | null>(null);

  useEffect(() => {
    dialSessionRef.current = dialSession;
  }, [dialSession]);

  const connectDevice = useCallback(async () => {
    setStatus("connecting");
    setError(null);
    try {
      const [{ token, configured }, cfgRes] = await Promise.all([
        getVoiceToken(),
        getCallConfig().catch(() => null),
      ]);
      setTwilioConfigured(configured);
      setShowConnectionErrors(
        Boolean(cfgRes?.config.preferences.showConnectionErrors),
      );
      if (!configured || !token) {
        setStatus("idle");
        return;
      }
      const { Device } = await import("@twilio/voice-sdk");
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
      const device = new Device(token, { logLevel: 1 });
      device.on("registered", () => setStatus("ready"));
      device.on("error", (err) => {
        const msg = err?.message ?? "Device error";
        setLastConnectionError(msg);
        if (showConnectionErrors) setError(msg);
        setStatus("error");
      });
      device.on("incoming", (conn) => {
        connectionRef.current = conn;
        setStatus("ringing");
        conn.accept();
        setStatus("in_call");
      });
      await device.register();
      deviceRef.current = device;
      setStatus("ready");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Connect failed";
      setError(msg);
      setLastConnectionError(msg);
      setStatus("error");
    }
  }, [showConnectionErrors]);

  useEffect(() => {
    void connectDevice();
    return () => {
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
  }, [connectDevice]);

  const dial = useCallback(
    async (req: DialRequest) => {
      setError(null);
      try {
        const { call, fromNumber, mode } = await prepareOutbound(req);
        setActiveCall(call);
        if (mode === "bridge") {
          setStatus("ringing");
          return;
        }
        if (!deviceRef.current || !twilioConfigured) {
          setStatus("post_call");
          setShowPostCall(true);
          setError(
            twilioConfigured
              ? "Softphone not ready — call logged for completion"
              : "Twilio not configured — call logged; complete purpose/disposition",
          );
          return;
        }
        const conn = await deviceRef.current.connect({
          params: {
            To: req.to,
            callId: call.callId,
            userId: call.userId,
            From: fromNumber ?? "",
          },
        });
        connectionRef.current = conn;
        setStatus("in_call");
        await updateCall(call.callId, {
          status: "in_progress",
          isActive: true,
          startedAt: new Date().toISOString(),
          twilioCallSid: conn.parameters.CallSid,
        });
        conn.on("disconnect", () => {
          connectionRef.current = null;
          setMuted(false);
          setShowPostCall(true);
          setStatus("post_call");
        });
        conn.on("error", (err) => {
          const msg = err?.message ?? "Call error";
          setLastConnectionError(msg);
          if (showConnectionErrors) setError(msg);
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Dial failed";
        setError(msg);
        setLastConnectionError(msg);
        setStatus("error");
      }
    },
    [twilioConfigured, showConnectionErrors],
  );

  const dialCurrentSessionTarget = useCallback(
    async (session: DialSession) => {
      const target = session.targets?.[session.currentIndex];
      const to = target?.to;
      if (!to) {
        setError(
          "Next dial target has no phone number — skip or cancel the session.",
        );
        return;
      }
      await dial({
        to,
        leadId: target.leadId,
        leadName: target.leadName,
        opportunityId: target.opportunityId,
        accountId: target.accountId,
      });
    },
    [dial],
  );

  const startSessionDialing = useCallback(
    async (session: DialSession) => {
      setDialSession(session);
      dialSessionRef.current = session;
      await dialCurrentSessionTarget(session);
    },
    [dialCurrentSessionTarget],
  );

  const skipDialSessionTarget = useCallback(async () => {
    const session = dialSessionRef.current;
    if (!session) return;
    const { session: next } = await advanceDialSession(session.sessionId);
    setDialSession(next.status === "completed" ? null : next);
    if (next.status === "completed") {
      setError(null);
      return;
    }
    await dialCurrentSessionTarget(next);
  }, [dialCurrentSessionTarget]);

  const cancelActiveDialSession = useCallback(async () => {
    const session = dialSessionRef.current;
    if (!session) return;
    await cancelDialSession(session.sessionId);
    setDialSession(null);
    dialSessionRef.current = null;
  }, []);

  const joinConference = useCallback(
    async (conferenceName: string, mode: CoachJoinMode, call: Call) => {
      setError(null);
      setActiveCall(call);
      if (!deviceRef.current || !twilioConfigured) {
        setError("Softphone not ready — cannot join conference");
        setStatus("error");
        return;
      }
      const conn = await deviceRef.current.connect({
        params: {
          conferenceName,
          coachMode: mode,
          callId: call.callId,
          userId: call.userId,
        },
      });
      connectionRef.current = conn;
      setStatus("in_call");
      conn.on("disconnect", () => {
        connectionRef.current = null;
        setMuted(false);
        setStatus(twilioConfigured ? "ready" : "idle");
      });
    },
    [twilioConfigured],
  );

  const hangup = useCallback(() => {
    connectionRef.current?.disconnect();
    connectionRef.current = null;
    setMuted(false);
    if (activeCall) {
      setShowPostCall(true);
      setStatus("post_call");
    } else {
      setStatus(twilioConfigured ? "ready" : "idle");
    }
  }, [activeCall, twilioConfigured]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    connectionRef.current?.mute(next);
    setMuted(next);
  }, [muted]);

  const sendDigits = useCallback((digits: string) => {
    connectionRef.current?.sendDigits(digits);
  }, []);

  const leaveVoicemail = useCallback(
    async (dropId: string) => {
      if (!activeCall) return;
      await dropVoicemail(activeCall.callId, dropId);
      hangup();
    },
    [activeCall, hangup],
  );

  const openPostCall = useCallback((call: Call) => {
    setActiveCall(call);
    setShowPostCall(true);
    setStatus("post_call");
  }, []);

  const savePostCall = useCallback(
    async (input: {
      purposeId?: string;
      dispositionId?: string;
      notes?: string;
    }) => {
      if (!activeCall) return;
      const completedId = activeCall.callId;
      await completeCall(completedId, input);
      setShowPostCall(false);
      setActiveCall(null);

      const session = dialSessionRef.current;
      if (session && session.status === "running") {
        const { session: next } = await advanceDialSession(
          session.sessionId,
          completedId,
        );
        if (next.status === "completed") {
          setDialSession(null);
          dialSessionRef.current = null;
          setStatus(twilioConfigured ? "ready" : "idle");
          return;
        }
        setDialSession(next);
        dialSessionRef.current = next;
        setStatus(twilioConfigured ? "ready" : "idle");
        await dialCurrentSessionTarget(next);
        return;
      }

      setStatus(twilioConfigured ? "ready" : "idle");
    },
    [activeCall, twilioConfigured, dialCurrentSessionTarget],
  );

  const dismissPostCall = useCallback(() => {
    setShowPostCall(false);
    setActiveCall(null);
    setStatus(twilioConfigured ? "ready" : "idle");
  }, [twilioConfigured]);

  return (
    <SoftphoneContext.Provider
      value={{
        status,
        twilioConfigured,
        error,
        muted,
        activeCall,
        dialSession,
        setDialSession,
        showPostCall,
        connectDevice,
        dial,
        hangup,
        toggleMute,
        sendDigits,
        leaveVoicemail,
        joinConference,
        startSessionDialing,
        skipDialSessionTarget,
        cancelActiveDialSession,
        openPostCall,
        savePostCall,
        dismissPostCall,
        lastConnectionError,
      }}
    >
      {children}
    </SoftphoneContext.Provider>
  );
}
